import Swal from 'sweetalert2';

export interface DeleteConfirmOptions {
  title?: string;
  text?: string;
  itemName?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

/**
 * Show a standardized delete confirmation dialog
 * @param options - Configuration options for the confirmation dialog
 * @returns Promise<boolean> - true if user confirmed, false if cancelled
 */
export const confirmDelete = async (options: DeleteConfirmOptions = {}): Promise<boolean> => {
  const {
    title = 'Delete Item?',
    text = 'This action cannot be undone. Are you sure you want to delete this item?',
    itemName,
    confirmButtonText = 'Yes, delete it',
    cancelButtonText = 'Cancel',
  } = options;

  const displayText = itemName
    ? `This action cannot be undone. Are you sure you want to delete "${itemName}"?`
    : text;

  const result = await Swal.fire({
    title,
    text: displayText,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280',
    confirmButtonText,
    cancelButtonText,
    customClass: {
      popup: 'rounded-2xl',
      title: 'text-2xl font-bold text-gray-900',
      confirmButton: 'rounded-lg px-6 py-3 font-semibold',
      cancelButton: 'rounded-lg px-6 py-3 font-semibold',
    },
  });

  return result.isConfirmed;
};

/**
 * Show success message after successful deletion
 * @param message - Success message to display
 */
export const showDeleteSuccess = (message: string = 'Item deleted successfully') => {
  Swal.fire({
    icon: 'success',
    title: 'Deleted!',
    text: message,
    timer: 2000,
    showConfirmButton: false,
    customClass: {
      popup: 'rounded-2xl',
    },
  });
};

/**
 * Show error message after failed deletion
 * @param message - Error message to display
 */
export const showDeleteError = (message: string = 'Failed to delete item') => {
  Swal.fire({
    icon: 'error',
    title: 'Delete Failed',
    text: message,
    confirmButtonColor: '#dc2626',
    customClass: {
      popup: 'rounded-2xl',
      confirmButton: 'rounded-lg px-6 py-3 font-semibold',
    },
  });
};
